import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Grid,
    Divider,
    IconButton,
    Stack
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams } from "react-router-dom";
import {
    getFirestore,
    doc,
    getDoc,
    collection,
    query,
    where,
    getDocs,
    addDoc,
    serverTimestamp,
    updateDoc,
    deleteDoc,
    Timestamp
} from "firebase/firestore";
import Fiananceheader from "./Fiananceheader";

const db = getFirestore();

function EmiDetails() {
    const { loanid } = useParams();
    const [loan, setLoan] = useState(null);
    const [emis, setEmis] = useState([]);
    const [loading, setLoading] = useState(true);

    // Popup & Form states
    const [openDialog, setOpenDialog] = useState(false);
    const [editEmiId, setEditEmiId] = useState(null); // Tracks if we are editing
    const [payDate, setPayDate] = useState("");
    const [amount, setAmount] = useState("");

    const fetchData = async () => {
        setLoading(true);
        try {
            const loanRef = doc(db, "loans", loanid);
            const loanSnap = await getDoc(loanRef);

            if (!loanSnap.exists()) {
                setLoan(null);
                setLoading(false);
                return;
            }

            const loanData = loanSnap.data();

            // Customer details
            const customerRef = doc(db, "fianancecustomers", loanData.custid);
            const customerSnap = await getDoc(customerRef);
            const customerData = customerSnap.exists()
                ? customerSnap.data()
                : { name: "Unknown", mobile: "N/A" };

            setLoan({
                id: loanid,
                ...loanData,
                customerName: customerData.name,
                customerMobile: customerData.mobile,
            });

            // Fetch EMIs
            const emiQuery = query(
                collection(db, "loanemis"),
                where("loanid", "==", loanid)
            );
            const emiSnap = await getDocs(emiQuery);
            const emiList = emiSnap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            emiList.sort((a, b) => (a.paydate?.seconds || 0) - (b.paydate?.seconds || 0));
            setEmis(emiList);
        } catch (error) {
            console.error("Error fetching EMI details:", error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [loanid]);

    // Helper function to dynamically check and sync loan open/close status
    const syncLoanStatus = async () => {
        const loanRef = doc(db, "loans", loanid);
        const loanSnap = await getDoc(loanRef);

        if (loanSnap.exists()) {
            const loanData = loanSnap.data();
            const emiQuery = query(collection(db, "loanemis"), where("loanid", "==", loanid));
            const emiSnap = await getDocs(emiQuery);
            const emiList = emiSnap.docs.map((doc) => doc.data());

            const totalPaid = emiList.reduce((sum, e) => sum + (e.amount || 0), 0);
            const remainingLoan = (loanData.loanamount || 0) - totalPaid;

            // Update status conditionally
            if (remainingLoan <= 0) {
                await updateDoc(loanRef, { status: "close" });
            } else {
                await updateDoc(loanRef, { status: "open" });
            }
        }
    };

    // Open dialog for Add mode
    const handleOpenAdd = () => {
        setEditEmiId(null);
        setAmount("");
        setPayDate("");
        setOpenDialog(true);
    };

    // Open dialog for Edit mode
    const handleOpenEdit = (emi) => {
        setEditEmiId(emi.id);
        setAmount(emi.amount);
        if (emi.paydate && emi.paydate.seconds) {
            const dateObj = new Date(emi.paydate.seconds * 1000);
            const formattedDate = dateObj.toISOString().split("T")[0]; // YYYY-MM-DD format
            setPayDate(formattedDate);
        } else {
            setPayDate("");
        }
        setOpenDialog(true);
    };

    // Save or Update EMI Handler
    const handleSaveEmi = async () => {
        if (!amount || !payDate) {
            alert("कृपया रक्कम व दिनांक भरा");
            return;
        }

        try {
            const emiData = {
                loanid: loanid,
                amount: Number(amount),
                paydate: Timestamp.fromDate(new Date(payDate)),
            };

            if (editEmiId) {
                // Update Existing EMI
                const emiRef = doc(db, "loanemis", editEmiId);
                await updateDoc(emiRef, emiData);
            } else {
                // Create New EMI
                await addDoc(collection(db, "loanemis"), {
                    ...emiData,
                    createdAt: serverTimestamp(),
                });
            }

            // Sync structural parameters
            await syncLoanStatus();
            setOpenDialog(false);
            setAmount("");
            setPayDate("");
            fetchData();
        } catch (error) {
            console.error("Error saving EMI:", error);
            alert("हप्ता जतन करण्यात अयशस्वी");
        }
    };

    // Delete EMI Handler
    const handleDeleteEmi = async (emiId) => {
        if (!window.confirm("तुम्हाला खात्री आहे की तुम्ही हा हप्ता हटवू इच्छिता?")) {
            return;
        }

        try {
            await deleteDoc(doc(db, "loanemis", emiId));
            await syncLoanStatus();
            fetchData();
        } catch (error) {
            console.error("Error deleting EMI:", error);
            alert("हप्ता हटवण्यात अयशस्वी");
        }
    };

    if (loading) {
        return <Typography>लोड होत आहे...</Typography>;
    }

    if (!loan) {
        return <Typography>कर्ज सापडले नाही.</Typography>;
    }
    const totalPaid = emis.reduce((sum, e) => sum + (e.amount || 0), 0);
    const remainingLoan = (loan.loanamount || 0) - totalPaid;

    const calculateEndDate = (startDate, days) => {
        if (!startDate || !startDate.seconds) return "-";
        const date = new Date(startDate.seconds * 1000);
        date.setDate(date.getDate() + days);
        return date.toLocaleDateString();
    };

    return (
        <Box p={3}>
            <Fiananceheader />

            {/* Loan Summary */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", color: "primary.main" }}>
                    कर्जाचा तपशील
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid className="row" spacing={2}>
                    <Grid className="col-sm-2 mb-1" item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, bgcolor: "#f0f4ff", borderRadius: 2, textAlign: "center" }}>
                            <Typography variant="subtitle2" color="textSecondary">कर्जदार</Typography>
                            <Typography variant="h6">{loan.customerName}</Typography>
                            <Typography variant="body2" color="textSecondary">{loan.customerMobile}</Typography>
                        </Paper>
                    </Grid>

                    <Grid className="col-sm-2 mb-1" item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, bgcolor: "#e6f7f1", borderRadius: 2, textAlign: "center" }}>
                            <Typography variant="subtitle2" color="textSecondary">मूळ कर्ज</Typography>
                            <Typography variant="h6">₹{loan.loanamount}</Typography>
                            <Typography variant="body2" color="textSecondary">दिनांक: {loan.loandate}</Typography>
                        </Paper>
                    </Grid>

                    <Grid className="col-sm-2 mb-1" item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, bgcolor: "#f2f7e6ff", borderRadius: 2, textAlign: "center" }}>
                            <Typography variant="subtitle2" color="textSecondary">कटिंग रक्कम </Typography>
                            <Typography variant="h6">₹{loan.amountcutting}</Typography>
                            <Typography variant="body2" color="textSecondary">-</Typography>
                        </Paper>
                    </Grid>

                    <Grid className="col-sm-2 mb-1" item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, bgcolor: "#f5e6f7ff", borderRadius: 2, textAlign: "center" }}>
                            <Typography variant="subtitle2" color="textSecondary">शेवट दिनांक</Typography>
                            <Typography variant="h6">{calculateEndDate(loan.loandate, 100)}</Typography>
                            <Typography variant="body2" color="textSecondary">(100 दिवस)</Typography>
                        </Paper>
                    </Grid>

                    <Grid className="col-sm-2 mb-1" item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, bgcolor: "#fff4e6", borderRadius: 2, textAlign: "center" }}>
                            <Typography variant="subtitle2" color="textSecondary">एकूण भरले</Typography>
                            <Typography variant="h6" color="success.main">₹{totalPaid}</Typography>
                            <Typography variant="body2" color="textSecondary">आजपर्यंत</Typography>
                        </Paper>
                    </Grid>

                    <Grid className="col-sm-2 mb-1" item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, bgcolor: "#ffe6e6", borderRadius: 2, textAlign: "center" }}>
                            <Typography variant="subtitle2" color="textSecondary">बाकी रक्कम</Typography>
                            <Typography variant="h6" color="error.main">₹{remainingLoan < 0 ? 0 : remainingLoan}</Typography>
                            <Typography variant="body2" color="textSecondary">स्थिती: {loan.status}</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>

            {/* Pay EMI Button */}
            <Box mb={2}>
                <Button variant="contained" color="primary" onClick={handleOpenAdd}>
                    हप्ता भरा
                </Button>
            </Box>

            {/* EMI Table Section */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", color: "primary.main" }}>
                हप्त्यांचा इतिहास
            </Typography>

            {emis.length === 0 ? (
                <Typography>हप्ते सापडले नाहीत.</Typography>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
                    <Table size="medium">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                <TableCell>क्र. नं.</TableCell>
                                <TableCell>भरण्याची तारीख</TableCell>
                                <TableCell>भरलेली रक्कम</TableCell>
                                <TableCell>उर्वरित रक्कम</TableCell>
                                <TableCell align="center">क्रिया (Actions)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {emis.reduce((rows, emi, index) => {
                                const paidSoFar = emis.slice(0, index + 1).reduce((sum, e) => sum + (e.amount || 0), 0);
                                const remaining = (loan.loanamount || 0) - paidSoFar;

                                rows.push(
                                    <TableRow key={emi.id} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                            {emi.paydate && new Date(emi.paydate.seconds * 1000).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>₹{emi.amount}</TableCell>
                                        <TableCell>₹{remaining < 0 ? 0 : remaining}</TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <IconButton 
                                                    color="primary" 
                                                    size="small" 
                                                    onClick={() => handleOpenEdit(emi)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton 
                                                    color="error" 
                                                    size="small" 
                                                    onClick={() => handleDeleteEmi(emi.id)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                                return rows;
                            }, [])}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pay / Edit EMI Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle>{editEmiId ? "हप्ता दुरुस्त करा" : "हप्ता भरा"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="भरण्याची तारीख"
                        type="date"
                        fullWidth
                        value={payDate}
                        onChange={(e) => setPayDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        margin="normal"
                    />
                    <TextField
                        label="रक्कम"
                        type="number"
                        fullWidth
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>रद्द करा</Button>
                    <Button onClick={handleSaveEmi} variant="contained" color="primary">
                        {editEmiId ? "अपडेट करा" : "जतन करा"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default EmiDetails;